import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  startVoiceSession,
  angelikaTurn,
  endVoiceSession,
  listSessionTranscripts,
} from "@/lib/angelika.functions";
import { runIntakeWorkflow } from "@/lib/orchestrator.functions";

export const Route = createFileRoute("/_authenticated/app/angelika")({
  component: AngelikaPage,
  errorComponent: ({ error }) => <div className="p-6 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-6 text-sm">Nicht gefunden.</div>,
});

function AngelikaPage() {
  const qc = useQueryClient();
  const start = useServerFn(startVoiceSession);
  const turn = useServerFn(angelikaTurn);
  const end = useServerFn(endVoiceSession);
  const list = useServerFn(listSessionTranscripts);
  const intake = useServerFn(runIntakeWorkflow);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);

  const transcripts = useQuery({
    queryKey: ["voice-tx", sessionId],
    queryFn: () => list({ data: { session_id: sessionId! } }),
    enabled: !!sessionId,
    refetchInterval: 2000,
  });

  const mStart = useMutation({
    mutationFn: () => start({ data: { language: "de" } }),
    onSuccess: (r: any) => setSessionId(r.session.id),
    onError: (e: Error) => toast.error(e.message),
  });
  const mTurn = useMutation({
    mutationFn: (text: string) =>
      turn({ data: { session_id: sessionId ?? undefined, user_text: text, source: "text" } }),
    onSuccess: (r: any) => {
      if (!sessionId) setSessionId(r.session_id);
      setInput("");
      qc.invalidateQueries({ queryKey: ["voice-tx"] });
      try {
        const u = new SpeechSynthesisUtterance(r.reply);
        u.lang = "de-DE";
        u.rate = 1.12;
        u.pitch = 1.05;
        window.speechSynthesis?.speak(u);
      } catch {}
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const mEnd = useMutation({
    mutationFn: () => end({ data: { session_id: sessionId! } }),
    onSuccess: (r: any) => toast.success(`Zusammenfassung erstellt (${r.summary?.length ?? 0} Zeichen)`),
  });
  const mIntake = useMutation({
    mutationFn: (projectId: string) => intake({ data: { project_id: projectId, triggered_by_session: sessionId ?? undefined } }),
    onSuccess: () => toast.success("Phase-1-Workflow gestartet"),
    onError: (e: Error) => toast.error(e.message),
  });

  useEffect(() => {
    const SR: any = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = "de-DE";
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e: any) => {
      const text = e.results?.[0]?.[0]?.transcript ?? "";
      if (text) mTurn.mutate(text);
    };
    r.onend = () => setListening(false);
    recRef.current = r;
    return () => {
      try { r.stop(); } catch {}
    };
  }, [sessionId]);

  function toggleMic() {
    const r = recRef.current;
    if (!r) {
      toast.error("Browser unterstützt keine Spracherkennung");
      return;
    }
    if (listening) {
      r.stop();
      setListening(false);
    } else {
      setListening(true);
      r.start();
    }
  }

  const [projectId, setProjectId] = useState("");

  return (
    <PortalShell nav={[{ to: "/app/portal", label: "Übersicht" }]}>
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Angelika · Sprach-Assistentin</h1>
          <p className="text-sm text-muted-foreground">
            Phase-1-Bedarfsaufnahme · Aussagen sind unverbindlich.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Sitzung {sessionId && <Badge variant="secondary">aktiv</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              {!sessionId && <Button onClick={() => mStart.mutate()} disabled={mStart.isPending}>Sitzung starten</Button>}
              {sessionId && (
                <>
                  <Button variant={listening ? "destructive" : "default"} onClick={toggleMic}>
                    {listening ? "Stop" : "🎤 Sprechen"}
                  </Button>
                  <Button variant="outline" onClick={() => mEnd.mutate()} disabled={mEnd.isPending}>Sitzung beenden</Button>
                </>
              )}
            </div>
            {sessionId && (
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Oder per Text mit Angelika sprechen…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && input.trim()) mTurn.mutate(input.trim());
                  }}
                />
                <Button onClick={() => input.trim() && mTurn.mutate(input.trim())} disabled={mTurn.isPending}>
                  Senden
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gesprächsprotokoll</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {(transcripts.data?.transcripts ?? []).map((t: any) => (
              <div key={t.id} className="rounded border p-2">
                <div className="text-xs text-muted-foreground">{t.role} · {new Date(t.created_at).toLocaleTimeString()}</div>
                <div>{t.text}</div>
              </div>
            ))}
            {(transcripts.data?.transcripts ?? []).length === 0 && (
              <p className="text-muted-foreground">Noch keine Äußerungen.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Mehragenten-Workflow starten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Analyse → Förderung → PV/WP → Steuer → Wirtschaftlichkeit → Dokumente → Aufgaben → Status.
            </p>
            <div className="flex gap-2">
              <Input value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Projekt-UUID" />
              <Button onClick={() => projectId && mIntake.mutate(projectId)} disabled={mIntake.isPending}>
                Phase-1-Intake starten
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalShell>
  );
}
